import { getActiveLocale } from "../i18n";
import { createLogger } from "../utils/logger";
import { API_BASE_URL } from "./apiClient";
import { APP_VERSION, CLIENT_PLATFORM, DEFAULT_DEVICE_LABEL } from "./clientMeta";
import { createQueuedTransport } from "./queuedTransport";

const logger = createLogger("analyticsClient");

export interface AnalyticsEventInput {
  feature: string;
  action: string;
  schemaVersion?: number;
  occurredAt?: string;
  resourceType?: string;
  resourceId?: string;
  workspaceId?: string;
  sessionId?: string | null;
  userState?: "anonymous" | "authenticated" | "admin" | "support";
  client?: Record<string, unknown>;
  properties?: Record<string, unknown>;
  ipRegion?: string;
  source?: string;
  experiment?: Record<string, unknown>;
}

interface AnalyticsEventPayload {
  event_id: string;
  occurred_at: string;
  schema_version: number;
  feature: string;
  action: string;
  workspace_id?: string;
  resource_type?: string;
  resource_id?: string;
  session_id?: string;
  request_id?: string;
  user_state?: string;
  client?: Record<string, unknown>;
  properties: Record<string, unknown>;
  ip_region?: string;
  source?: string;
  experiment?: Record<string, unknown>;
}

interface TrackOptions {
  accessToken?: string | null;
  sessionId?: string | null;
  userState?: AnalyticsEventInput["userState"];
  requestId?: string;
}

const STORAGE_KEY = "lotlocator:analytics-queue";
const MAX_BATCH_SIZE = 20;
const FLUSH_INTERVAL_MS = 1500;

const transport = createQueuedTransport<AnalyticsEventPayload>({
  storageKey: STORAGE_KEY,
  maxBatchSize: MAX_BATCH_SIZE,
  flushIntervalMs: FLUSH_INTERVAL_MS,
  logger,
  postBatch: (events, auth) => postEvents(events, auth.accessToken ?? null)
});

function safeRandomUUID(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function buildEvent(input: AnalyticsEventInput, opts: TrackOptions): AnalyticsEventPayload {
  const occurredAt = input.occurredAt ?? new Date().toISOString();
  const schemaVersion = input.schemaVersion ?? 1;
  const properties = input.properties ?? {};

  const client = {
    platform: CLIENT_PLATFORM,
    version: APP_VERSION,
    locale: getActiveLocale(),
    device: DEFAULT_DEVICE_LABEL,
    ...(input.client ?? {})
  };

  const userState = input.userState ?? opts.userState ?? "anonymous";

  return {
    event_id: safeRandomUUID(),
    occurred_at: occurredAt,
    schema_version: schemaVersion,
    feature: input.feature,
    action: input.action,
    workspace_id: input.workspaceId,
    resource_type: input.resourceType,
    resource_id: input.resourceId,
    session_id: opts.sessionId ?? undefined,
    request_id: opts.requestId,
    user_state: userState,
    client,
    properties,
    ip_region: input.ipRegion,
    source: input.source ?? "frontend",
    experiment: input.experiment
  };
}

async function postEvents(events: AnalyticsEventPayload[], accessToken: string | null) {
  if (events.length === 0) return;

  const body = JSON.stringify({ events });
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "Accept-Language": getActiveLocale()
  };
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const res = await fetch(`${API_BASE_URL}/analytics/events`, {
    method: "POST",
    body,
    headers,
    credentials: "include",
    keepalive: true
  });

  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || `analytics ingestion failed with ${res.status}`);
  }
}

export function trackAnalyticsEvent(input: AnalyticsEventInput, opts: TrackOptions = {}) {
  if (opts.accessToken !== undefined) {
    transport.setAuthToken(opts.accessToken);
  }
  const event = buildEvent(input, opts);
  transport.enqueue(event);
}
